/**
 * Error handling utilities for consistent error messages
 */

import { AxiosError } from 'axios'

/**
 * Extract error message from various error types
 * @param error - Error object from API calls, try-catch, etc.
 * @param fallbackMessage - Default message if extraction fails
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown, fallbackMessage = 'Ocorreu um erro inesperado'): string {
  // Axios error with response
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>

    // API returned error message
    if (axiosError.response?.data) {
      const data = axiosError.response.data
      if (typeof data === 'string') {
        return data
      }
      if (data.message) {
        return data.message
      }
      if (data.error) {
        return data.error
      }
    }

    // Network error
    if (axiosError.message === 'Network Error') {
      return 'Erro de conexão. Verifique sua internet.'
    }

    // Timeout error
    if (axiosError.code === 'ECONNABORTED') {
      return 'Tempo de conexão esgotado. Tente novamente.'
    }

    // HTTP status messages
    if (axiosError.response?.status) {
      switch (axiosError.response.status) {
        case 400:
          return 'Dados inválidos. Verifique as informações.'
        case 401:
          return 'Não autorizado. Faça login novamente.'
        case 403:
          return 'Acesso negado.'
        case 404:
          return 'Recurso não encontrado.'
        case 409:
          return 'Conflito. O recurso já existe.'
        case 422:
          return 'Dados de entrada inválidos.'
        case 429:
          return 'Muitas tentativas. Aguarde um momento.'
        case 500:
          return 'Erro no servidor. Tente novamente mais tarde.'
        case 503:
          return 'Serviço temporariamente indisponível.'
        default:
          return `Erro ${axiosError.response.status}: ${axiosError.message}`
      }
    }

    // Generic axios error
    return axiosError.message || fallbackMessage
  }

  // Standard Error object
  if (error instanceof Error) {
    return error.message || fallbackMessage
  }

  // String error
  if (typeof error === 'string') {
    return error
  }

  // Unknown error type
  return fallbackMessage
}

/**
 * Format validation errors from API
 * @param error - Error object that may contain validation errors
 * @returns Formatted validation error message
 */
export function getValidationErrors(error: unknown): string | null {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<{
      message?: string
      errors?: Record<string, string[]>
      validationErrors?: Array<{ field: string; message: string }>
    }>

    const data = axiosError.response?.data

    // Array of validation errors
    if (data?.validationErrors && Array.isArray(data.validationErrors)) {
      return data.validationErrors
        .map(err => `${err.field}: ${err.message}`)
        .join('\n')
    }

    // Object with field errors
    if (data?.errors && typeof data.errors === 'object') {
      return Object.entries(data.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n')
    }
  }

  return null
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError
    return axiosError.response?.status === 401 || axiosError.response?.status === 403
  }
  return false
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError
    return axiosError.message === 'Network Error' || axiosError.code === 'ECONNABORTED'
  }
  return false
}
